import React, { useState, Fragment, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Box, Paper, Grid, Typography, Avatar, IconButton, Autocomplete, Chip, TextField,
  FormControl, InputLabel, Select, MenuItem, Switch, Collapse, Divider, Button,
  Radio, RadioGroup, FormControlLabel, ListSubheader, InputAdornment, Tooltip,
  SvgIcon, TextareaAutosize, Tabs, Tab, Snackbar,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useTheme, alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import GitHubIcon from '@mui/icons-material/GitHub';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useHistory } from 'react-router-dom';
import Toast from '../actions/Toasts';
import InputController from '../../controllers/InputController';
import PreferredEmailPicker from './PreferredEmailPicker';
import apiService, { authFetch } from '../../services/apiServices';
import { workTypeOptions } from '../../utils/workTypeOptions';
import { departmentsForBranch } from '../../utils/departmentBranches';
import configService from '../../services/configService';
import ModuleCustomFields from '../dynamic/ModuleCustomFields';
import { ROLES, ROLES_CONFIG } from '../../config/permissions/rolesConfig';
import usePermissions from '../../hooks/usePermissions';
// I njëjti bllok prezence si te profili — një komponent, jo dy kopje pamjeje
// që rrëshqasin nga njëra-tjetra.
import AttendanceAccess from './AttendanceAccess';
import ActivationDateHint, { AccountActivationNote } from './ActivationDateHint';

// ─────────────────────────────────────────────────────────────────────────────
// Single-step worker registration — a SUPERSET of AddWorker + AddWorkerContract.
//
// Lives at /workerlist/register — a SEPARATE page from the legacy AddWorker
// (/workerlist/addworker), which stays untouched. Every function of both legacy
// forms is present here; one "Ruaj" runs the whole chain:
//   1. POST /api/workers            → worker (+ employee_code hook)
//   2. POST /api/workerContract     → contract, if that section is ON
//   3. POST /api/users (+ link + invite) → optional login account
//   4. configService.saveCustomFieldValues → module custom fields
// then redirects to the worker profile.
// ─────────────────────────────────────────────────────────────────────────────

const SlackIcon = (props) => (
  <SvgIcon viewBox="0 0 122 122" {...props}>
    <path fill="#E01E5A" d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" />
    <path fill="#36C5F0" d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" />
    <path fill="#2EB67D" d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" />
    <path fill="#ECB22E" d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" />
  </SvgIcon>
);

// Only FirstName + LastName are hard requirements. Email becomes required lazily
// (see onSubmit) only when a login account is requested.
const schema = Yup.object().shape({
  FirstName: Yup.string().trim().required('Emri është i detyrueshëm'),
  LastName: Yup.string().trim().required('Mbiemri është i detyrueshëm'),
  Email: Yup.string().email('Email-i nuk është valid').nullable().transform((v) => v || null),
  WorkEmail: Yup.string().email('Email-i i punës nuk është valid').nullable().transform((v) => v || null),
});

export default function RegisterWorker() {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const { roleKeys, hasPermission } = usePermissions();
  // Pasqyrim i gardhit të serverit: pa `attendance.manage` kanalet nuk preken
  // as këtu, as me kërkesë të drejtpërdrejtë (POST /api/workers → 403).
  const canManageAttendance = hasPermission('attendance.manage');

  const [toast, setToast] = useState({ open: false, message: '', variant: '' });
  const [submitting, setSubmitting] = useState(false);
  const [professionEdited, setProfessionEdited] = useState(false);
  const [tab, setTab] = useState(0);

  // Reference data
  const [allWorkers, setAllWorkers] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const fiscalDefaultsRef = useRef({ employee: 5, employer: 5, brutoNeto: 1 });

  // Structured selections
  const [supervisor, setSupervisor] = useState(null);
  const [deptValues, setDeptValues] = useState([]);
  const [projectValues, setProjectValues] = useState([]);
  const [secondJob, setSecondJob] = useState(false);
  const [residency, setResidency] = useState('resident');
  // `payBasis` e jo `salaryType`: ai emër është zënë nga lloji i pagës së
  // kontratës (mujore/orare). Ky tregon nëse shuma është bruto apo neto.
  const [payBasis, setPayBasis] = useState('gross');
  const [hasTimeTracker, setHasTimeTracker] = useState(true);
  const [attendanceEnabled, setAttendanceEnabled] = useState(true);
  // `null`, JO `[]`: punëtori i ri nuk ka konfigurim të vetin dhe ndjek
  // parazgjedhjen e kompanisë. Një listë bosh është vendim tjetër — «asnjë
  // kanal i lejuar» — dhe do t'ia mbyllte prezencën që nga dita e parë.
  const [attendanceChannels, setAttendanceChannels] = useState(null);
  const [kioskScanAllowed, setKioskScanAllowed] = useState(true);
  const [kioskScanNote, setKioskScanNote] = useState('');
  const [customValues, setCustomValues] = useState({});
  const [socialNetworks, setSocialNetworks] = useState([]);
  const [insMembers, setInsMembers] = useState([]);
  const [capAlert, setCapAlert] = useState({ open: false, available: 0, over: 0 });

  // Contract sub-form (ON by default)
  const [contractOn, setContractOn] = useState(true);
  const [workType, setWorkType] = useState(null);
  const [branch, setBranch] = useState(null);
  const [salaryType, setSalaryType] = useState(null);
  const [brutoNetoVal, setBrutoNetoVal] = useState(null);
  const [contractExtVal, setContractExtVal] = useState(null);
  const [templateVal, setTemplateVal] = useState(null);
  const [withTax, setWithTax] = useState(true);

  // Project assignment (optional) — POSTed after the worker is created
  const [assignOn, setAssignOn] = useState(false);
  const [assign, setAssign] = useState({ project_id: '', start_date: '', end_date: '', hours_per_day: 8 });

  // System access
  const [createAccount, setCreateAccount] = useState(false);
  const [accountRole, setAccountRole] = useState(ROLES.EMPLOYEE);
  const [sendInvite, setSendInvite] = useState(true);
  // Kur dërgohet ftesa: parambushet nga plani i serverit (një ditë pune para
  // fillimit, fillimi i orarit) dhe ndryshohet me dorë (`manual`).
  const [inviteAt, setInviteAt] = useState({ date: '', time: '', manual: false });
  const [roleSearch, setRoleSearch] = useState('');

  const {
    register, handleSubmit, control, setValue, watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { WithTax: true, EmployeeTax: 5, EmployerTax: 5, PreferredEmail: 'personal' },
  });

  // ── Role picker (search + descriptions + permission chips) ──────────────────
  const canAssignAnyRole =
    roleKeys.includes(ROLES.COMPANY_ADMIN) || roleKeys.includes(ROLES.SUPER_ADMIN);
  const assignableRoles = useMemo(() => {
    const all = Object.values(ROLES_CONFIG).slice().sort((a, b) => a.priority - b.priority);
    return canAssignAnyRole ? all : all.filter((r) => r.key === ROLES.EMPLOYEE);
  }, [canAssignAnyRole]);
  const filteredAssignableRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return assignableRoles;
    return assignableRoles.filter((r) =>
      [r.label, r.description, r.key].some((f) => (f || '').toLowerCase().includes(q)));
  }, [assignableRoles, roleSearch]);
  const selectedRoleConfig = ROLES_CONFIG[accountRole];

  // ── Static option lists ─────────────────────────────────────────────────────
  const salaryTypes = useMemo(() => ([
    { id: 1, name: t('worker.monthly', 'Mujore') },
    { id: 2, name: t('worker.byHour', 'Në orë') },
  ]), [t]);
  const brutoNetoOptions = useMemo(() => ([
    { id: 1, name: t('worker.bruto', 'Bruto') },
    { id: 2, name: t('worker.neto', 'Neto') },
  ]), [t]);
  const contractExtensionOptions = useMemo(() => ([
    { id: 1, name: t('worker.monthly', 'Mujore') },
    { id: 2, name: t('worker.yearly', 'Vjetore') },
  ]), [t]);

  const socialPlatforms = useMemo(() => ([
    { id: 'slack',     labelKey: 'worker.socialSlack',     Icon: SlackIcon,     color: '#4A154B', placeholder: '@username · workspace.slack.com', notify: true },
    { id: 'whatsapp',  labelKey: 'worker.socialWhatsapp',  Icon: WhatsAppIcon,  color: '#25D366', placeholder: '+383 44 …', notify: true },
    { id: 'telegram',  labelKey: 'worker.socialTelegram',  Icon: TelegramIcon,  color: '#229ED9', placeholder: '@username', notify: true },
    { id: 'linkedin',  labelKey: 'worker.socialLinkedin',  Icon: LinkedInIcon,  color: '#0A66C2', placeholder: 'linkedin.com/in/…' },
    { id: 'facebook',  labelKey: 'worker.socialFacebook',  Icon: FacebookIcon,  color: '#1877F2', placeholder: 'facebook.com/…' },
    { id: 'instagram', labelKey: 'worker.socialInstagram', Icon: InstagramIcon, color: '#E4405F', placeholder: '@username' },
    { id: 'twitter',   labelKey: 'worker.socialTwitter',   Icon: TwitterIcon,   color: '#1DA1F2', placeholder: '@username' },
    { id: 'github',    labelKey: 'worker.socialGithub',    Icon: GitHubIcon,    color: '#24292F', placeholder: 'github.com/…' },
    { id: 'website',   labelKey: 'worker.socialWebsite',   Icon: LanguageIcon,  color: '#64748B', placeholder: 'https://…' },
    { id: 'other',     labelKey: 'worker.socialOther',     Icon: LinkIcon,      color: '#94A3B8', placeholder: 'https://…' },
  ]), []);

  const addSocialNetwork = () => {
    const used = new Set(socialNetworks.map((s) => s.platform));
    const next = socialPlatforms.find((p) => !used.has(p.id)) || socialPlatforms[0];
    setSocialNetworks((prev) => [...prev, { platform: next.id, value: '' }]);
  };
  const removeSocialNetwork = (idx) => setSocialNetworks((prev) => prev.filter((_, i) => i !== idx));
  const updateSocialNetwork = (idx, field, val) =>
    setSocialNetworks((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: val } : s)));

  // Insured family members. Each carries the same fields as the primary insured
  // (card number, name, start date, amount) plus the family relationship.
  const addInsMember = () => setInsMembers((prev) => [...prev, { cardNumber: '', name: '', relation: '', startDate: '', endDate: '', amount: '' }]);
  const removeInsMember = (idx) => setInsMembers((prev) => prev.filter((_, i) => i !== idx));
  const updateInsMember = (idx, field, val) =>
    setInsMembers((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: val } : m)));

  const suggestProfession = (q) => {
    const val = String(q || '').toLowerCase();
    if (!val) return '';
    if (val.includes('matemat')) return 'Matematikan';
    if (val.includes('informatik')) return 'Programer / Inxhinier Softueri';
    if (val.includes('ekonom')) return 'Ekonomist';
    if (val.includes('jurid')) return 'Jurist';
    return '';
  };

  // ── Load reference data + fiscal defaults ───────────────────────────────────
  const loadFiscalDefaults = useCallback(async () => {
    let defaults = { employee: 5, employer: 5, brutoNeto: 1 };
    try {
      const res = await apiService().get('fiscalPolitics');
      const fp = res?.success && Array.isArray(res.data) && res.data.length ? res.data[0] : null;
      if (fp) {
        defaults = {
          employee: Number(fp.EmployeeTax ?? fp.employeeTax ?? 5),
          employer: Number(fp.EmployerTax ?? fp.employerTax ?? 5),
          brutoNeto: fp.Type ?? fp.type ?? 1,
        };
      }
    } catch (e) { /* non-critical */ }
    fiscalDefaultsRef.current = defaults;
    setValue('EmployeeTax', defaults.employee);
    setValue('EmployerTax', defaults.employer);
    setBrutoNetoVal(brutoNetoOptions.find((o) => String(o.id) === String(defaults.brutoNeto)) || brutoNetoOptions[0]);
  }, [setValue, brutoNetoOptions]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await loadFiscalDefaults();
      if (!mounted) return;
      try {
        const [wRes, dRes, wtRes, bRes, tRes, pRes] = await Promise.all([
          apiService().get('workers'),
          apiService().get('department'),
          apiService().get('worktype'),
          apiService().get('branch'),
          apiService().get('contractsTemplates'),
          apiService().get('projects'),
        ]);
        if (!mounted) return;
        if (wRes?.success) setAllWorkers(wRes.data || []);
        if (dRes?.success) setAllDepartments((dRes.data || []).filter((d) => d.Active !== false));
        if (pRes?.success) setAllProjects(pRes.data || []);
        setWorkTypes(workTypeOptions(wtRes?.success ? wtRes.data : []));
        if (bRes?.success) setBranches((bRes.data || []).filter((b) => b.Active !== false).map((b, i) => ({
          id: b?.id ?? b?.Id ?? i + 1, name: b?.name ?? b?.Name ?? '',
        })).filter((b) => b.name));
        if (tRes?.success) setTemplates((tRes.data || []).map((d, i) => ({
          id: d?.Id ?? d?.id ?? i + 1, name: d?.Name ?? d?.name ?? d?.Title ?? d?.title ?? '',
        })).filter((d) => d.name));
      } catch (e) { /* non-critical */ }
    })();
    return () => { mounted = false; };
  }, [loadFiscalDefaults]);

  const setImage = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setValue('Image', 'data:image/png;base64,' + reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  };

  // ── The one orchestrated submit ─────────────────────────────────────────────
  const onSubmit = async (data) => {
    if (createAccount && !String(data.WorkEmail || data.Email || '').trim()) {
      setToast({ open: true, message: 'Për të krijuar qasje në sistem, email-i është i detyrueshëm.', variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      // 1) Worker
      const cleanedSocial = socialNetworks
        .map((s) => ({ platform: s.platform, value: String(s.value || '').trim() }))
        .filter((s) => s.value);
      const workerPayload = {
        FirstName: data.FirstName,
        LastName: data.LastName,
        PersonalId: data.PersonalId || null,
        ParentName: data.ParentName || null,
        Birthday: data.Birthday || null,
        Qualification: data.Qualification || null,
        Gender: data.Gender ?? null,
        MaritalStatus: data.MaritalStatus ?? null,
        Email: data.Email || null,
        WorkEmail: data.WorkEmail || null,
        PreferredEmail: data.PreferredEmail === 'work' ? 'work' : 'personal',
        Phone: data.Phone || null,
        Address: data.Address || null,
        Place: data.Place || null,
        PostalCode: data.PostalCode || null,
        Country: data.Country || null,
        Position: data.Position || null,
        StartDate: data.StartDate || null,
        EmploymentEndDate: data.EmploymentEndDate || null,
        ActivationDate: data.ActivationDate || null,
        Image: data.Image || '',
        SupervisorId: supervisor ? (supervisor.id || supervisor.Id) : null,
        DepartmentIds: deptValues.map((d) => d.id || d.Id).filter(Boolean),
        ProjectIds: projectValues.map((p) => p.id || p.Id).filter(Boolean),
        // Regjistrimi i prezencës — të katër fushat, si te profili.
        AttendanceEnabled: attendanceEnabled,
        AttendanceChannels: attendanceChannels,
        KioskScanAllowed: kioskScanAllowed,
        KioskScanNote: kioskScanAllowed ? null : (kioskScanNote || null),
        HasTimeTracker: hasTimeTracker,
        SocialNetworks: cleanedSocial.length ? JSON.stringify(cleanedSocial) : null,
        // Bank
        BankName: data.BankName || null,
        BankAccount: data.BankAccount || null,
        SecondJob: secondJob,
        Residency: residency,
        SalaryType: payBasis,
        // ATK
        ATKRegistered: !!data.ATKRegistered,
        ATKRegisteredAt: data.ATKRegisteredAt || null,
        // Separation
        SeparationType: data.SeparationType || null,
        SeparationReason: data.SeparationReason || null,
        // Health insurance
        HealthInsTotal: data.HealthInsTotal || 0,
        HealthInsPersonal: data.HealthInsPersonal || 0,
        HealthInsFamily: insMembers.reduce((s, m) => s + (Number(m.amount) || 0), 0) || 0,
        HealthInsNumber: data.HealthInsNumber || null,
        HealthInsHolderName: data.HealthInsHolderName || null,
        HealthInsIssued: data.HealthInsIssued || null,
        HealthInsExpires: data.HealthInsExpires || null,
        HealthInsMembers: (() => {
          const filled = insMembers.filter((m) => String(m.name || '').trim());
          return filled.length ? JSON.stringify(filled) : null;
        })(),
        // Work permit
        WorkPermitNumber: data.WorkPermitNumber || null,
        WorkPermitIssued: data.WorkPermitIssued || null,
        WorkPermitExpires: data.WorkPermitExpires || null,
      };
      if (workerPayload.DepartmentIds.length) workerPayload.Department = workerPayload.DepartmentIds[0];

      const wRes = await apiService().post('workers', workerPayload);
      if (!wRes?.success) {
        setToast({ open: true, message: 'Krijimi i punëtorit dështoi. Provo përsëri.', variant: 'error' });
        setSubmitting(false);
        return;
      }
      const newId = wRes?.data?.[0]?.id || wRes?.data?.id || wRes?.data?.[0]?.Id || wRes?.data?.Id;
      if (!newId) {
        setToast({ open: true, message: 'Punëtori u krijua por ID mungon në përgjigje.', variant: 'warning' });
        setSubmitting(false);
        return;
      }

      const outcomes = [];
      let inviteNote = '';

      // 2) Contract (optional)
      if (contractOn) {
        try {
          const contractPayload = {
            WorkerId: newId,
            StartDate: data.StartDate || null,
            EndDate: data.EndDate || null,
            WorkType: workType ? (workType.id || workType.Id) : null,
            BranchId: branch ? (branch.id || branch.Id) : null,
            Department: deptValues.length ? (deptValues[0].id || deptValues[0].Id) : null,
            SalaryType: salaryType ? (salaryType.id || salaryType.Id) : null,
            BaseSalary: data.BaseSalary || null,
            BrutoNeto: brutoNetoVal ? (brutoNetoVal.id || brutoNetoVal.Id) : null,
            HourSalary: data.HourSalary || null,
            HourSalaryWeekend: data.HourSalaryWeekend || null,
            HourSalaryHoliday: data.HourSalaryHoliday || null,
            EmployeeTax: withTax ? (data.EmployeeTax || fiscalDefaultsRef.current.employee) : 0,
            EmployerTax: withTax ? (data.EmployerTax || fiscalDefaultsRef.current.employer) : 0,
            ContractType: contractExtVal ? (contractExtVal.id || contractExtVal.Id) : null,
            Template: templateVal ? (templateVal.id || templateVal.Id) : null,
            TermsOfContract: data.TermsOfContract || '',
            createdBy: '',
          };
          const cRes = await apiService().post('workerContract', contractPayload);
          outcomes.push(cRes?.success ? 'contract-ok' : 'contract-fail');
        } catch (e) { outcomes.push('contract-fail'); }
      }

      // 3) System access (optional)
      if (createAccount) {
        try {
          const uRes = await apiService().post('users', {
            // Llogaria e hyrjes merr email-in e zgjedhur (bosh → tjetri).
            Email: String((data.PreferredEmail === 'work' ? data.WorkEmail : data.Email) || data.WorkEmail || data.Email).trim(),
            Name: data.FirstName || '',
            Surname: data.LastName || '',
            Role: accountRole,
            Phone: data.Phone || '',
            JobTitle: data.Position || data.Profession || '',
          });
          const userId = uRes?.data?.id || uRes?.data?.Id || uRes?.data?.[0]?.id || uRes?.data?.[0]?.Id;
          if (userId) {
            try { await apiService().put(`users/${userId}`, { worker_id: newId }); } catch (e) { /* keep going */ }
            if (sendInvite) {
              // Me datë aktivizimi në të ardhmen serveri e planifikon ftesën
              // (një ditë pune para fillimit) dhe e thotë te `message`.
              try {
                // Ora e zgjedhur me dorë shkon te serveri; pa të, ai e llogarit vetë.
                const body = inviteAt.manual && inviteAt.time ? { sendTime: inviteAt.time } : {};
                const ir = await apiService().post(`users/${userId}/send-invite`, body);
                if (ir?.scheduled) inviteNote = ir.message || '';
              } catch (e) { /* keep going */ }
            }
            outcomes.push(sendInvite ? (inviteNote ? 'account-scheduled' : 'account-invited') : 'account-ok');
          } else {
            outcomes.push('account-fail');
          }
        } catch (e) { outcomes.push('account-fail'); }
      }

      // 4) Project assignment (optional)
      if (assignOn && assign.project_id && assign.start_date) {
        try {
          const aRes = await authFetch(`/api/workers/${newId}/project-assignments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              project_id: Number(assign.project_id),
              start_date: assign.start_date,
              end_date: assign.end_date || null,
              hours_per_day: +assign.hours_per_day || 8,
            }),
          });
          const aData = await aRes.json().catch(() => ({}));
          outcomes.push(aRes.ok && aData.success ? 'assign-ok' : 'assign-fail');
        } catch (e) { outcomes.push('assign-fail'); }
      }

      // 5) Custom fields
      if (Object.keys(customValues).some((k) => customValues[k] !== '' && customValues[k] != null)) {
        try { await configService.saveCustomFieldValues('workers', newId, customValues); }
        catch (e) { /* non-critical */ }
      }

      const failed = outcomes.some((o) => o.endsWith('-fail'));
      setToast({
        open: true,
        message: failed
          ? 'Punëtori u regjistrua, por disa hapa dështuan — kontrollo profilin.'
          : `Punëtori u regjistrua me sukses.${inviteNote ? ` ${inviteNote}` : ''}`,
        variant: failed ? 'warning' : 'success',
      });
      setTimeout(() => { window.location.href = `#/workerlist/worker/${newId}`; }, 700);
    } catch (e) {
      setToast({ open: true, message: 'Ndodhi një gabim gjatë regjistrimit.', variant: 'error' });
      setSubmitting(false);
    }
  };

  // Validation lives on the "Bazike" tab (FirstName/LastName/Email); jump there
  // if the user submits from another tab with those still invalid.
  const onInvalid = (errs) => {
    if (errs.FirstName || errs.LastName || errs.Email) setTab(0);
  };
  const submit = () => (submitting ? undefined : handleSubmit(onSubmit, onInvalid)());

  const sectionSx = (color) => ({
    mb: 1.25, p: { xs: 1.25, sm: 1.5 }, borderRadius: 2,
    bgcolor: alpha(color, 0.04), border: '1px solid', borderColor: alpha(color, 0.18),
  });
  const sectionTitleSx = (color) => ({
    fontWeight: 700, fontSize: { xs: '0.8125rem', md: '0.875rem' }, mb: 1,
    display: 'flex', alignItems: 'center', gap: 0.6, color,
  });

  // Insurance budget: a total amount that is split across the primary insured
  // (worker) + family members. Allocations may not exceed the total.
  const insTotal = Number(watch('HealthInsTotal')) || 0;
  const insMainAmount = Number(watch('HealthInsPersonal')) || 0;
  const insMembersTotal = insMembers.reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const insAllocated = insMainAmount + insMembersTotal;
  const insRemaining = insTotal > 0 ? Math.max(0, insTotal - insAllocated) : 0;
  const insOver = insTotal > 0 && insAllocated > insTotal + 0.001;
  // Percentage base = the total when set, otherwise the allocated sum.
  const insBase = insTotal > 0 ? insTotal : insAllocated;
  const insPct = (x) => (insBase > 0 ? Math.round(((Number(x) || 0) / insBase) * 1000) / 10 : 0);
  // Clamp a candidate allocation so worker + members never exceed the total.
  const insMaxFor = (excludeMemberIdx, excludePrimary) => {
    if (insTotal <= 0) return Infinity;
    const others = (excludePrimary ? 0 : insMainAmount)
      + insMembers.reduce((s, m, i) => (i === excludeMemberIdx ? s : s + (Number(m.amount) || 0)), 0);
    return Math.max(0, insTotal - others);
  };
  const notifyOverCap = (available, over) => setCapAlert({ open: true, available, over });
  // Keep the raw typed string so entering amounts (decimals, clearing) stays
  // natural; only clamp + warn when the numeric value truly exceeds the cap.
  const setPrimaryAmount = (raw) => {
    const max = insMaxFor(-1, true);
    const n = Number(raw);
    if (Number.isFinite(n) && n > max) {
      notifyOverCap(max, n - max);
      setValue('HealthInsPersonal', max);
    }
  };
  const setMemberAmount = (idx, raw) => {
    const max = insMaxFor(idx, false);
    const n = Number(raw);
    if (Number.isFinite(n) && n > max) {
      notifyOverCap(max, n - max);
      updateInsMember(idx, 'amount', String(max));
    } else {
      updateInsMember(idx, 'amount', raw);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8f9fc', minHeight: '100vh', py: { xs: 0.5, md: 0.75 }, px: { xs: 1, sm: 1.5, md: 2 } }}>
      <Fragment>
        <Toast {...toast} />
        <Snackbar
          open={capAlert.open}
          autoHideDuration={4500}
          onClose={() => setCapAlert((a) => ({ ...a, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: { xs: 8, sm: 9 } }}
        >
          <Paper
            elevation={8}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
              borderRadius: 2.5, maxWidth: 500,
              border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.4),
              borderLeft: `4px solid ${theme.palette.warning.main}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
            }}
          >
            <Box sx={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.warning.main, 0.16) }}>
              <WarningAmberRoundedIcon sx={{ color: theme.palette.warning.dark, fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
                Shuma u kufizua në maksimumin e lejuar
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                Lejohet deri në <Box component="span" sx={{ fontWeight: 800, color: 'warning.dark' }}>{capAlert.available.toFixed(2)} €</Box> për këtë fushë · nga {insTotal.toFixed(2)} € total.
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setCapAlert((a) => ({ ...a, open: false }))} sx={{ ml: 0.5, color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Snackbar>

        <form>
          <Paper sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            {/* Header — compact single row with inline actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar src={watch('Image')} sx={{ width: 40, height: 40, border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                  <PersonIcon sx={{ fontSize: 22 }} />
                </Avatar>
                <input accept="image/*" style={{ display: 'none' }} id="imageUpload" type="file" onChange={setImage} />
                <label htmlFor="imageUpload">
                  <IconButton component="span" size="small" sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, width: 18, height: 18 }}>
                    <PhotoCameraIcon sx={{ fontSize: 11 }} />
                  </IconButton>
                </label>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.2 }}>
                  Regjistrim i punëtorit — një hap
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.3 }}>
                  Një "Ruaj" krijon punëtorin, kontratën, qasjen dhe caktimin.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => history.goBack()} sx={{ textTransform: 'none' }}>
                  Kthehu
                </Button>
                <Button size="small" variant="contained" color="success" startIcon={<SaveIcon />} disabled={submitting} onClick={submit} sx={{ textTransform: 'none' }}>
                  Ruaj
                </Button>
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.25 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 0.5 } }}>
                <Tab label="Të dhëna bazike" sx={{ textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Punësimi & Kontrata" sx={{ textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Dokumente & Qasje" sx={{ textTransform: 'none', fontWeight: 600 }} />
              </Tabs>
            </Box>

            {/* ── Tab 0: Të dhëna bazike ─────────────────────────────────────── */}
            <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
            {/* Personal */}
            <Box sx={sectionSx(theme.palette.primary.main)}>
              <Typography variant="subtitle2" sx={sectionTitleSx('primary.main')}>
                <PersonIcon sx={{ fontSize: 18 }} /> Të dhëna personale
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Numri Personal / Pasaporta" error={errors.PersonalId} control={control} register={{ ...register('PersonalId') }} name="PersonalId" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Emri" error={errors.FirstName} control={control} register={{ ...register('FirstName') }} name="FirstName" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Mbiemri" error={errors.LastName} control={control} register={{ ...register('LastName') }} name="LastName" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Emri i prindit" control={control} register={{ ...register('ParentName') }} name="ParentName" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Datelindja" control={control} type="date" register={{ ...register('Birthday') }} name="Birthday" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController
                    placeholder="Kualifikimi" control={control} register={{ ...register('Qualification') }} name="Qualification"
                    changeHandler={(e) => {
                      const currentProf = watch('Profession');
                      if (!currentProf || !professionEdited) {
                        const s = suggestProfession(e?.target?.value);
                        if (s) setValue('Profession', s);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <InputController placeholder="Profesioni (propozohet nga shkollimi)" control={control} register={{ ...register('Profession') }} name="Profession" onFieldChange={() => setProfessionEdited(true)} />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <Controller defaultValue="" name="Gender" control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel value="0" control={<Radio size="small" />} label={t('worker.female', 'Femër')} />
                        <FormControlLabel value="1" control={<Radio size="small" />} label={t('worker.male', 'Mashkull')} />
                      </RadioGroup>
                    )} />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <Controller defaultValue="" name="MaritalStatus" control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel value="0" control={<Radio size="small" />} label={t('worker.single', 'Beqar/e')} />
                        <FormControlLabel value="1" control={<Radio size="small" />} label={t('worker.married', 'Martuar')} />
                      </RadioGroup>
                    )} />
                </Grid>
              </Grid>
            </Box>

            {/* Contact */}
            <Box sx={sectionSx(theme.palette.success.main)}>
              <Typography variant="subtitle2" sx={sectionTitleSx('success.dark')}>
                <ContactMailIcon sx={{ fontSize: 18 }} /> Kontakti
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <InputController placeholder="Email personal" error={errors.Email} control={control} register={{ ...register('Email') }} name="Email" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputController placeholder="Email i punës (kompanisë)" error={errors.WorkEmail} control={control} register={{ ...register('WorkEmail') }} name="WorkEmail" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputController placeholder="Telefoni" control={control} register={{ ...register('Phone') }} name="Phone" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PreferredEmailPicker control={control} watch={watch} />
                </Grid>
              </Grid>
            </Box>

            {/* Address */}
            <Box sx={sectionSx(theme.palette.warning.main)}>
              <Typography variant="subtitle2" sx={sectionTitleSx('warning.dark')}>
                <LocationOnIcon sx={{ fontSize: 18 }} /> Adresa
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12}>
                  <InputController placeholder="Adresa" control={control} register={{ ...register('Address') }} name="Address" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputController placeholder="Qyteti" control={control} register={{ ...register('Place') }} name="Place" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <InputController placeholder="Kodi Postal" control={control} register={{ ...register('PostalCode') }} name="PostalCode" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <InputController placeholder="Shteti" control={control} register={{ ...register('Country') }} name="Country" />
                </Grid>
              </Grid>
            </Box>

            {/* Bank */}
            <Box sx={sectionSx(theme.palette.info.main)}>
              <Typography variant="subtitle2" sx={sectionTitleSx('info.dark')}>
                <AccountBalanceRoundedIcon sx={{ fontSize: 18 }} /> Banka
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <InputController placeholder="Emri i bankës" control={control} register={{ ...register('BankName') }} name="BankName" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputController placeholder="Xhirollogaria" control={control} register={{ ...register('BankAccount') }} name="BankAccount" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Paga dakorduar</InputLabel>
                    <Select value={payBasis} label="Paga dakorduar" onChange={(e) => setPayBasis(e.target.value)}>
                      <MenuItem value="gross">Bruto</MenuItem>
                      <MenuItem value="net">Neto</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rezidenca</InputLabel>
                    <Select value={residency} label="Rezidenca" onChange={(e) => setResidency(e.target.value)}>
                      <MenuItem value="resident">Vendas</MenuItem>
                      <MenuItem value="foreign">Punëtor i huaj</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {/* Caktohet që në regjistrim: ndryshon tatimin te lista e
                      pagave, dhe zbulimi i tij pas pagës së parë do të thoshte
                      rillogaritje. Sekondari tatohet pa brezin e liruar. */}
                  <FormControlLabel
                    control={<Switch checked={secondJob} onChange={(e) => setSecondJob(e.target.checked)} />}
                    label={(
                      <Typography variant="caption">
                        {secondJob ? 'Punë sekondare' : 'Punë primare'}
                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                          {secondJob ? 'tatohet pa brezin e liruar' : 'brezat tatimorë zbatohen normalisht'}
                        </Typography>
                      </Typography>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Social networks */}
            <Box sx={sectionSx(theme.palette.secondary.main)}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: socialNetworks.length ? 1.5 : 0.5, gap: 1 }}>
                <Typography variant="subtitle2" sx={{ ...sectionTitleSx('secondary.dark'), mb: 0 }}>
                  <ShareIcon sx={{ fontSize: 18 }} /> {t('worker.socialNetworks', 'Rrjete sociale')}
                </Typography>
                <Button size="small" variant="outlined" color="secondary" startIcon={<AddIcon />} onClick={addSocialNetwork} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                  {t('worker.addSocialNetwork', 'Shto rrjet')}
                </Button>
              </Box>
              {socialNetworks.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {socialNetworks.map((sn, idx) => {
                    const platform = socialPlatforms.find((p) => p.id === sn.platform) || socialPlatforms[0];
                    return (
                      <Box key={`${sn.platform}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 0.75 }}>
                        <FormControl size="small" sx={{ minWidth: { xs: 150, sm: 200 } }}>
                          <Select value={sn.platform} onChange={(e) => updateSocialNetwork(idx, 'platform', e.target.value)}
                            renderValue={(val) => {
                              const p = socialPlatforms.find((x) => x.id === val) || socialPlatforms[0];
                              const Ic = p.Icon;
                              return (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><Ic sx={{ fontSize: 18, color: p.color }} /><span>{t(p.labelKey)}</span></Box>);
                            }}
                            sx={{ borderRadius: 1.5, bgcolor: 'white' }}>
                            {socialPlatforms.map((p) => {
                              const Ic = p.Icon;
                              return (
                                <MenuItem key={p.id} value={p.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                                    <Ic sx={{ fontSize: 18, color: p.color }} /><span>{t(p.labelKey)}</span>
                                    {p.notify && (<Chip label={t('worker.socialNotifyHint', 'njoftim')} size="small" sx={{ ml: 'auto', height: 18, fontSize: '0.65rem', bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.dark', '& .MuiChip-label': { px: 0.75 } }} />)}
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                        <TextField size="small" fullWidth value={sn.value} onChange={(e) => updateSocialNetwork(idx, 'value', e.target.value)} placeholder={platform.placeholder} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'white' } }} />
                        <Tooltip title={t('worker.socialRemove', 'Hiq')}>
                          <IconButton size="small" onClick={() => removeSocialNetwork(idx)} sx={{ color: 'error.main' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            </Box>{/* end Tab 0 */}

            {/* ── Tab 1: Punësimi & Kontrata ─────────────────────────────────── */}
            <Box sx={{ display: tab === 1 ? 'block' : 'none' }}>
            {/* Employment */}
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <Typography variant="subtitle2" sx={{ ...sectionTitleSx('#1D4ED8') }}>
                <BusinessCenterIcon sx={{ fontSize: 16 }} /> Punësimi
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Pozita" control={control} register={{ ...register('Position') }} name="Position" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Data e fillimit" control={control} type="date" register={{ ...register('StartDate') }} name="StartDate" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Data e mbarimit të punësimit" control={control} type="date" register={{ ...register('EmploymentEndDate') }} name="EmploymentEndDate" />
                </Grid>
                {/* ── Data e aktivizimit ──────────────────────────────────
                    Punëtori regjistrohet sot, fillon më vonë. Deri atë ditë
                    llogaria (nëse krijohet te «Qasja në sistem») nuk hyn dot —
                    serveri e krahason datën në çdo hyrje dhe çdo kërkesë, jo
                    ndonjë job. Bosh = qasje menjëherë, si më parë. */}
                <Grid item xs={12} sm={6} md={4}>
                  <InputController placeholder="Data e aktivizimit" control={control} type="date" register={{ ...register('ActivationDate') }} name="ActivationDate" />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <ActivationDateHint value={watch('ActivationDate')} hasAccount={createAccount} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete options={allWorkers} value={supervisor} onChange={(_, v) => setSupervisor(v)}
                    getOptionLabel={(o) => `${o.FirstName || ''} ${o.LastName || ''}`.trim()} size="small"
                    renderInput={(params) => <TextField {...params} label="Mbikëqyrësi" size="small" />} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete multiple options={departmentsForBranch(allDepartments, branch?.id ?? branch?.Id)} value={deptValues} onChange={(_, v) => setDeptValues(v)}
                    getOptionLabel={(o) => o.Name || o.name || ''} size="small"
                    renderTags={(val, getTagProps) => val.map((opt, idx) => (<Chip key={opt.Id || opt.id || idx} label={opt.Name || opt.name || ''} size="small" {...getTagProps({ index: idx })} />))}
                    renderInput={(params) => <TextField {...params} label="Departamentet" size="small" />} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete multiple options={allProjects} value={projectValues} onChange={(_, v) => setProjectValues(v)}
                    getOptionLabel={(o) => o.Name || o.name || ''} size="small"
                    renderTags={(val, getTagProps) => val.map((opt, idx) => (<Chip key={opt.Id || opt.id || idx} label={opt.Name || opt.name || ''} size="small" {...getTagProps({ index: idx })} />))}
                    renderInput={(params) => <TextField {...params} label="Projektet" size="small" />} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Controller name="SeparationType" control={control} defaultValue=""
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Lloji i ndarjes</InputLabel>
                        <Select {...field} value={field.value || ''} label="Lloji i ndarjes">
                          <MenuItem value="">{t('common.none', 'Asnjë')}</MenuItem>
                          <MenuItem value="resigned">{t('worker.separationResigned', 'Dorëheqje')}</MenuItem>
                          <MenuItem value="terminated">{t('worker.separationTerminated', 'Largim')}</MenuItem>
                        </Select>
                      </FormControl>
                    )} />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <Controller name="SeparationReason" control={control} defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} value={field.value || ''} label="Arsyeja e ndarjes" size="small" fullWidth multiline minRows={1} maxRows={3} />
                    )} />
                </Grid>
                {/* ── Regjistrimi i Prezencës ──────────────────────────────
                    I njëjti bllok si te profili — literalisht i njëjti
                    komponent, jo një kopje e dytë e tij. Këtu rrinte vetëm një
                    çelës «Time Tracker», pa të drejtën kryesore dhe pa kanale,
                    dhe punëtori i regjistruar dilte pa asnjë vendim prezence.

                    Kanalet nisin `null` — ndiqet parazgjedhja e kompanisë
                    derisa dikush ta prekë listën. */}
                <Grid item xs={12}>
                  <AttendanceAccess
                    canManage={canManageAttendance}
                    value={{
                      enabled: attendanceEnabled,
                      channels: attendanceChannels,
                      kioskScanAllowed: kioskScanAllowed,
                      kioskScanNote: kioskScanNote,
                      hasTimeTracker: hasTimeTracker,
                    }}
                    onChange={(patch) => {
                      if ('enabled' in patch) setAttendanceEnabled(patch.enabled);
                      if ('channels' in patch) setAttendanceChannels(patch.channels);
                      if ('kioskScanAllowed' in patch) setKioskScanAllowed(patch.kioskScanAllowed);
                      if ('kioskScanNote' in patch) setKioskScanNote(patch.kioskScanNote);
                      if ('hasTimeTracker' in patch) setHasTimeTracker(patch.hasTimeTracker);
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Contract (collapsible) */}
            <Box sx={sectionSx(theme.palette.info.main)}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ ...sectionTitleSx('info.dark'), mb: 0 }}>
                  <DescriptionRoundedIcon sx={{ fontSize: 18 }} /> Kontrata
                </Typography>
                <FormControlLabel control={<Switch checked={contractOn} onChange={(e) => setContractOn(e.target.checked)} />} label={<Typography variant="caption">Krijo kontratën tani</Typography>} />
              </Box>
              <Collapse in={contractOn}>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputController placeholder="Data e mbarimit" control={control} type="date" register={{ ...register('EndDate') }} name="EndDate" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete options={workTypes} value={workType} onChange={(_, v) => setWorkType(v)} getOptionLabel={(o) => o?.name || ''} size="small" renderInput={(params) => <TextField {...params} label="Lloji i punës" size="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete options={branches} value={branch} onChange={(_, v) => setBranch(v)} getOptionLabel={(o) => o?.name || ''} size="small" renderInput={(params) => <TextField {...params} label="Filiali" size="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete options={salaryTypes} value={salaryType} onChange={(_, v) => setSalaryType(v)} getOptionLabel={(o) => o?.name || ''} size="small" renderInput={(params) => <TextField {...params} label="Lloji i pagës" size="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputController placeholder="Paga bazë" control={control} symbol="€" register={{ ...register('BaseSalary') }} name="BaseSalary" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete options={brutoNetoOptions} value={brutoNetoVal} onChange={(_, v) => setBrutoNetoVal(v)} getOptionLabel={(o) => o?.name || ''} size="small" renderInput={(params) => <TextField {...params} label="Bruto/Neto" size="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputController placeholder="Paga në orë" control={control} symbol="€" register={{ ...register('HourSalary') }} name="HourSalary" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputController placeholder="Paga në orë (fundjavë)" control={control} symbol="€" register={{ ...register('HourSalaryWeekend') }} name="HourSalaryWeekend" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InputController placeholder="Paga në orë (festa)" control={control} symbol="€" register={{ ...register('HourSalaryHoliday') }} name="HourSalaryHoliday" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel control={<Switch checked={withTax} onChange={(e) => setWithTax(e.target.checked)} />} label={<Typography variant="caption">Me kontribut</Typography>} />
                  </Grid>
                  {withTax && (
                    <>
                      <Grid item xs={6} sm={3} md={2}>
                        <InputController placeholder="Kontr. punëtori" control={control} symbol="%" type="number" register={{ ...register('EmployeeTax') }} name="EmployeeTax" />
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <InputController placeholder="Kontr. punëdhënësi" control={control} symbol="%" type="number" register={{ ...register('EmployerTax') }} name="EmployerTax" />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete options={contractExtensionOptions} value={contractExtVal} onChange={(_, v) => setContractExtVal(v)} getOptionLabel={(o) => o?.name || ''} size="small" renderInput={(params) => <TextField {...params} label="Zgjatja e kontratës" size="small" />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete options={templates} value={templateVal} onChange={(_, v) => setTemplateVal(v)} getOptionLabel={(o) => o?.name || ''} size="small" renderInput={(params) => <TextField {...params} label="Template kontrate" size="small" />} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextareaAutosize className="custom-textarea" placeholder="Kushtet e kontratës" style={{ width: '100%' }} minRows={4} {...register('TermsOfContract')} onChange={(e) => setValue('TermsOfContract', e.target.value)} />
                  </Grid>
                </Grid>
              </Collapse>
            </Box>

            {/* Assign to project (collapsible) */}
            <Box sx={sectionSx(theme.palette.secondary.main)}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ ...sectionTitleSx('secondary.dark'), mb: 0 }}>
                  <BusinessCenterIcon sx={{ fontSize: 18 }} /> Cakto punëtorin në projekt
                </Typography>
                <FormControlLabel control={<Switch checked={assignOn} onChange={(e) => setAssignOn(e.target.checked)} />} label={<Typography variant="caption">Cakto tani</Typography>} />
              </Box>
              <Collapse in={assignOn}>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Projekti</InputLabel>
                      <Select value={assign.project_id} label="Projekti" onChange={(e) => setAssign((a) => ({ ...a, project_id: e.target.value }))}>
                        <MenuItem value=""><em>— Zgjidhni —</em></MenuItem>
                        {allProjects.map((p) => (
                          <MenuItem key={p.Id || p.id} value={p.Id || p.id}>{p.Name || p.name || `#${p.Id || p.id}`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField fullWidth size="small" type="number" label="Orë/ditë" InputLabelProps={{ shrink: true }} value={assign.hours_per_day} onChange={(e) => setAssign((a) => ({ ...a, hours_per_day: e.target.value }))} inputProps={{ min: 0.5, max: 8, step: 0.5 }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="date" label="Data e fillimit" InputLabelProps={{ shrink: true }} value={assign.start_date} onChange={(e) => setAssign((a) => ({ ...a, start_date: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="date" label="Data e mbarimit (opsionale)" InputLabelProps={{ shrink: true }} value={assign.end_date} onChange={(e) => setAssign((a) => ({ ...a, end_date: e.target.value }))} inputProps={{ min: assign.start_date || undefined }} />
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Punëtori mund të caktohet në disa projekte; totali i orëve/ditë nëpër caktimet aktive nuk kalon 8h.
                </Typography>
              </Collapse>
            </Box>

            </Box>{/* end Tab 1 */}

            {/* ── Tab 2: Dokumente & Qasje ───────────────────────────────────── */}
            <Box sx={{ display: tab === 2 ? 'block' : 'none' }}>
            {/* ATK Registration */}
            <Box sx={sectionSx(theme.palette.info.main)}>
              <Typography variant="subtitle2" sx={sectionTitleSx('info.dark')}>
                <BadgeRoundedIcon sx={{ fontSize: 18 }} /> Regjistrimi në ATK
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <InputController placeholder="Data e regjistrimit në ATK" control={control} type="date" register={{ ...register('ATKRegisteredAt') }} name="ATKRegisteredAt" />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Controller name="ATKRegistered" control={control} defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch size="small" checked={!!field.value}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked && !watch('ATKRegisteredAt')) {
                              const d = new Date();
                              setValue('ATKRegisteredAt', `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`);
                            }
                            field.onChange(checked);
                          }} />}
                        label="I regjistruar në ATK"
                      />
                    )} />
                </Grid>
              </Grid>
            </Box>

            {/* Health insurance */}
            <Box sx={{ ...sectionSx(theme.palette.warning.main), '& .MuiInputBase-input': { py: '7px', fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.82rem' } }}>
              <Typography variant="subtitle2" sx={sectionTitleSx('warning.dark')}>
                <MedicalServicesOutlinedIcon sx={{ fontSize: 18 }} /> Sigurimi shëndetësor
              </Typography>

              {/* Total budget — split across worker + family, capped at this amount */}
              <Grid container spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ mb: 1.25 }}>
                <Grid item xs={12} sm={4}>
                  <InputController placeholder="Shuma totale e sigurimit" control={control} type="number" symbol="€" rules={{ required: false }} register={{ ...register('HealthInsTotal') }} name="HealthInsTotal" />
                </Grid>
                {insTotal > 0 && (
                  <Grid item xs={12} sm={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`E ndarë: ${insAllocated.toFixed(2)} €`} sx={{ height: 22, fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.dark' }} />
                      <Chip size="small" label={`E mbetur: ${insRemaining.toFixed(2)} €`} sx={{ height: 22, fontWeight: 700, bgcolor: insOver ? alpha(theme.palette.error.main, 0.15) : alpha(theme.palette.success.main, 0.14), color: insOver ? 'error.main' : 'success.dark' }} />
                      {insOver && <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>Ndarja tejkalon totalin!</Typography>}
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Primary insured card */}
              <Box sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1, py: 0.75, mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>I siguruari kryesor (punëtori)</Typography>
                  {insBase > 0 && (
                    <Chip size="small" label={`${insPct(insMainAmount)}%`} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.15), color: 'warning.dark' }} />
                  )}
                </Box>
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  <Grid item xs={12} sm={3}>
                    <InputController placeholder="Numri i kartelës" control={control} register={{ ...register('HealthInsNumber') }} name="HealthInsNumber" />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <InputController placeholder="Emri i të siguruarit" control={control} register={{ ...register('HealthInsHolderName') }} name="HealthInsHolderName" />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <InputController placeholder="Data e fillimit" control={control} type="date" register={{ ...register('HealthInsIssued') }} name="HealthInsIssued" />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <InputController placeholder="Data e përfundimit" control={control} type="date" rules={{ required: false }} register={{ ...register('HealthInsExpires') }} name="HealthInsExpires" />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <InputController placeholder="Shuma" control={control} type="number" symbol="€" rules={{ required: false }} register={{ ...register('HealthInsPersonal') }} name="HealthInsPersonal" changeHandler={(e) => setPrimaryAmount(e?.target?.value)} />
                  </Grid>
                </Grid>
              </Box>

              {/* Family members */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: insMembers.length ? 1 : 0, gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 15 }} /> Sigurimi për familjarë
                  {insMembers.length > 0 && (
                    <Chip label={insMembers.length} size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }} />
                  )}
                </Typography>
                <Button size="small" variant="outlined" color="warning" startIcon={<AddIcon />} onClick={addInsMember} sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                  Shto familjar
                </Button>
              </Box>
              {insMembers.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {insMembers.map((m, idx) => (
                    <Box key={idx} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1, py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <PersonIcon sx={{ fontSize: 13 }} /> Familjar {idx + 1}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {insBase > 0 && <Chip size="small" label={`${insPct(m.amount)}%`} sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(theme.palette.warning.main, 0.14), color: 'warning.dark' }} />}
                          <IconButton size="small" onClick={() => removeInsMember(idx)} sx={{ color: 'error.main', p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <TextField size="small" fullWidth label="Emri i të siguruarit" value={m.name} onChange={(e) => updateInsMember(idx, 'name', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Lidhja familjare</InputLabel>
                            <Select value={m.relation} label="Lidhja familjare" onChange={(e) => updateInsMember(idx, 'relation', e.target.value)}>
                              <MenuItem value="spouse">Bashkëshort/e</MenuItem>
                              <MenuItem value="child">Fëmijë</MenuItem>
                              <MenuItem value="parent">Prind</MenuItem>
                              <MenuItem value="sibling">Vëlla/Motër</MenuItem>
                              <MenuItem value="other">Tjetër</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <TextField size="small" fullWidth label="Nr. kartelës" value={m.cardNumber} onChange={(e) => updateInsMember(idx, 'cardNumber', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={6} md={4} lg={2}>
                          <TextField size="small" fullWidth type="date" label="Data e fillimit" InputLabelProps={{ shrink: true }} value={m.startDate} onChange={(e) => updateInsMember(idx, 'startDate', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={6} md={4} lg={2}>
                          <TextField size="small" fullWidth type="date" label="Data e përfundimit" InputLabelProps={{ shrink: true }} value={m.endDate} onChange={(e) => updateInsMember(idx, 'endDate', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                          <TextField size="small" fullWidth type="number" label="Shuma" value={m.amount} onChange={(e) => setMemberAmount(idx, e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Split summary */}
              {insBase > 0 && (
                <Box sx={{ mt: 1.25, p: 1, borderRadius: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                    {insTotal > 0 ? `Totali: ${insTotal.toFixed(2)} € · E ndarë: ${insAllocated.toFixed(2)} €` : `Totali i sigurimit: ${insAllocated.toFixed(2)} €`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Punëtori {insPct(insMainAmount)}%{insMembers.length ? ` · Familjarë ${insPct(insMembersTotal)}%` : ''}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Work permit */}
            <Box sx={sectionSx(theme.palette.success.main)}>
              <Typography variant="subtitle2" sx={sectionTitleSx('success.dark')}>
                <VerifiedUserRoundedIcon sx={{ fontSize: 18 }} /> Leja e punës
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12} sm={4}>
                  <InputController placeholder="Numri i lejes" control={control} register={{ ...register('WorkPermitNumber') }} name="WorkPermitNumber" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputController placeholder="Data e lëshimit" control={control} type="date" register={{ ...register('WorkPermitIssued') }} name="WorkPermitIssued" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputController placeholder="Data e skadimit" control={control} type="date" register={{ ...register('WorkPermitExpires') }} name="WorkPermitExpires" />
                </Grid>
              </Grid>
            </Box>

            {/* System access (optional) — full role picker */}
            <Box sx={sectionSx(theme.palette.primary.main)}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.dark', mt: 0.25 }}>
                    <SecurityRoundedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xs: '0.875rem', md: '0.9375rem' }, color: 'primary.dark', lineHeight: 1.25 }}>
                      {t('worker.systemAccess', 'Qasja në sistem')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}>
                      {t('worker.systemAccessHint', 'Krijo një llogari hyrjeje për këtë punëtor dhe cakto rolin.')}
                    </Typography>
                  </Box>
                </Box>
                <Switch checked={createAccount} onChange={(e) => setCreateAccount(e.target.checked)} color="primary" />
              </Box>
              <Collapse in={createAccount}>
                <Box sx={{ mt: 1.75 }}>
                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    <Grid item xs={12} sm={7}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="reg-role-label">{t('worker.role', 'Roli')}</InputLabel>
                        <Select labelId="reg-role-label" label={t('worker.role', 'Roli')} value={accountRole}
                          onChange={(e) => setAccountRole(e.target.value)} onClose={() => setRoleSearch('')}
                          MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 480 } } }}
                          renderValue={(value) => {
                            const r = ROLES_CONFIG[value];
                            if (!r) return value;
                            return (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} /><Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.label}</Typography></Box>);
                          }}>
                          <ListSubheader sx={{ p: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', lineHeight: 1 }}>
                            <TextField size="small" autoFocus fullWidth placeholder={t('worker.roleSearchPlaceholder', 'Kërko rol…')} value={roleSearch}
                              onChange={(e) => setRoleSearch(e.target.value)}
                              onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }} />
                          </ListSubheader>
                          {filteredAssignableRoles.length === 0 && (
                            <MenuItem disabled sx={{ py: 1.5, justifyContent: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('worker.roleSearchNoResults', 'Nuk u gjet asnjë rol')}</Typography>
                            </MenuItem>
                          )}
                          {filteredAssignableRoles.map((r) => (
                            <MenuItem key={r.key} value={r.key} sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.25 }}>{r.label}</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>{r.description}</Typography>
                                </Box>
                                <Chip size="small" label={`${r.permissions.length}`} sx={{ ml: 1, height: 20, fontSize: '0.65rem', borderRadius: 1, fontWeight: 700, bgcolor: r.bgColor, color: r.color }} />
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {selectedRoleConfig && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.75, lineHeight: 1.45 }}>
                          {selectedRoleConfig.permissions.length} {t('worker.rolePrivileges', 'privilegje aktive për këtë rol.')}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Switch checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} color="primary" sx={{ mt: -0.5 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{t('worker.sendInvite', 'Dërgo ftesë me email')}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, lineHeight: 1.4 }}>{t('worker.sendInviteHint', 'Punëtori merr një link për të vendosur password-in.')}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1, lineHeight: 1.45 }}>
                    {t('worker.accountEmailHint', 'Llogaria përdor email-in nga seksioni i kontaktit.')}
                  </Typography>
                  {/* Data e aktivizimit, e vendosur te «Punësimi»: ftesa del sot, por
                      hyrja hapet atë ditë. Thuhet KËTU, ku HR-ja vendos për ftesën,
                      jo vetëm te fusha — përndryshe duket sikur ftesa e hap llogarinë. */}
                  <AccountActivationNote value={watch('ActivationDate')} sendInvite={sendInvite} onChange={(v) => setValue('ActivationDate', v || '', { shouldDirty: true })} inviteAt={inviteAt} onInviteAtChange={setInviteAt} />
                </Box>
              </Collapse>
            </Box>

            {/* Custom fields */}
            <ModuleCustomFields moduleKey="workers" mode="create" values={customValues} onChange={setCustomValues} />
            </Box>{/* end Tab 2 */}

            {/* Save row — always visible under the tabs */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0, 1, 2].map((i) => (
                  <Box key={i} onClick={() => setTab(i)} sx={{ width: 8, height: 8, borderRadius: '50%', cursor: 'pointer', bgcolor: tab === i ? 'primary.main' : 'action.disabled' }} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {tab < 2 && (
                  <Button variant="outlined" onClick={() => setTab((v) => Math.min(2, v + 1))}>
                    Vazhdo
                  </Button>
                )}
                <Button variant="contained" color="success" disabled={submitting} onClick={submit}>
                  {submitting ? 'Duke ruajtur…' : 'Regjistro punëtorin'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </form>
      </Fragment>
    </Box>
  );
}
